import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import JsonLd from "@/components/base/JsonLd";
import { useState } from "react";

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormError("");
      return;
    }

    const formData = new FormData(form);
    formData.delete("phone_alt");

    try {
      const res = await fetch("https://readdy.ai/api/form/d9oouqiftmtnfcfh0beg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (!res.ok || !parsed || parsed.code !== "OK") {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || "提交失败，请稍后再试");
        setFormSubmitted(false);
        return;
      }

      setFormSubmitted(true);
      setFormError("");
      form.reset();
    } catch {
      setFormError("网络错误，请稍后再试");
      setFormSubmitted(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于皮皮漫画网",
          "url": "https://pipimanhua.com/about",
          "description": "皮皮漫画网专注日韩与国漫精品推荐，了解品牌介绍、口碑评价与联系方式。",
          "isPartOf": {
            "@type": "WebSite",
            "name": "皮皮漫画网",
            "url": "https://pipimanhua.com/",
          },
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "皮皮漫画网涵盖哪些漫画类型？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "皮皮漫画网涵盖韩漫推荐、日漫系列和国漫精选三大品类，包括恋爱甜宠、热血战斗、奇幻冒险、玄幻修仙等多种类型，满足不同读者的多样化需求。",
              },
            },
            {
              "@type": "Question",
              "name": "皮皮漫画网的漫画多久更新一次？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "皮皮漫画网的韩漫排行榜实时更新，日漫和国漫第一时间同步最新章节，确保漫画迷能够及时追更，不错过任何精彩内容。",
              },
            },
            {
              "@type": "Question",
              "name": "皮皮漫画网是否免费？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "皮皮漫画网提供免费的漫画推荐和资讯服务，用户可以在平台上浏览漫画信息、查看排行榜和阅读推荐内容。",
              },
            },
          ],
        }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
              关于皮皮漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-3 max-w-2xl mx-auto">
              专注日韩与国漫精品推荐，为漫画爱好者提供最优质的阅读体验和追更服务
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-16">
            <div>
              <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">品牌介绍</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                皮皮漫画网成立于2024年，是一个专注日韩与国漫精品推荐的漫画资讯平台。我们致力于为广大漫画爱好者提供最新、最全、最优质的漫画资源推荐服务。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                平台涵盖韩漫推荐、日漫系列、国漫精选三大板块，覆盖恋爱甜宠、热血战斗、奇幻冒险、玄幻修仙等多种类型。韩漫排行榜实时更新，日漫系列全集下拉畅读，国漫大图高清全彩，满足不同读者的多样化需求。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们拥有一支专业的漫画编辑团队，每日精选优质漫画内容，确保推荐质量。资源全面、更新快速，让每一位漫画迷都能在这里找到心仪的作品，享受畅快的追更体验。
              </p>
            </div>

            <div>
              <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">我们的优势</h2>
              <div className="space-y-4">
                {[
                  {
                    icon: "ri-star-line",
                    title: "精选推荐",
                    desc: "编辑团队精心筛选，只推荐高质量漫画作品，拒绝低质内容",
                  },
                  {
                    icon: "ri-flashlight-line",
                    title: "实时更新",
                    desc: "韩漫排行榜实时刷新，日漫国漫第一时间同步更新，追更不等待",
                  },
                  {
                    icon: "ri-layout-grid-line",
                    title: "三大品类",
                    desc: "韩漫、日漫、国漫全覆盖，满足不同读者的阅读偏好",
                  },
                  {
                    icon: "ri-eye-line",
                    title: "高清大图",
                    desc: "国漫大图全彩高清呈现，日漫下拉畅读体验流畅，视觉享受满分",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-accent-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            {[
              { num: "10000+", label: "漫画资源" },
              { num: "500万+", label: "月活跃用户" },
              { num: "99.5%", label: "用户满意度" },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl">
                <div className="text-3xl font-bold text-primary-500 font-heading">{stat.num}</div>
                <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-background-100 rounded-xl p-6 md:p-8" id="contact">
            <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading text-center">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-500">contact@pipimanhua.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">微信公众号</p>
                    <p className="text-sm text-foreground-500">皮皮漫画网</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">办公地址</p>
                    <p className="text-sm text-foreground-500">中国·杭州</p>
                  </div>
                </div>
              </div>

              <div>
                {formSubmitted && !formError ? (
                  <div className="bg-accent-100 rounded-lg p-6 text-center">
                    <i className="ri-checkbox-circle-line text-4xl text-accent-600"></i>
                    <p className="text-sm font-medium text-accent-800 mt-2">感谢您的留言！</p>
                    <p className="text-xs text-accent-600 mt-1">我们会尽快回复您</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder="电子邮箱"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                        />
                      </div>
                      <input
                        type="text"
                        name="subject"
                        placeholder="主题"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的留言内容……"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      ></textarea>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                      {formError && (
                        <p className="text-xs text-red-500 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}